import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { translator } from 'mage-i18n';
import { Column, Row } from 'mage-components';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { REGIONS } from '@/constants';
import SummaryField from '@/components/SummaryField';
import { getMerchantCountryKey, merchantRegion } from '@/selectors/merchant';
import { hasAddressData } from '@/utils/address';
import * as personalIdUtils from '@/utils/personalId';
import * as dateUtils from '@/utils/date';

import { CY_APPLICATION_OVERVIEW_PAGE_IDS } from './constants';

const {
  ADDRESS_COUNTRY,
  CITY,
  DATE_OF_BIRTH,
  FIRST_NAME,
  LAST_NAME,
  PERSONAL_ID,
  POSTAL_CODE,
  REGION,
  STREET,
} = CY_APPLICATION_OVERVIEW_PAGE_IDS;

const tKey = 'products-fe.components.stakeholders.form';

const SummaryPerson = ({
  merchantCountry,
  stakeholder: { data, hasPersonalId = false },
  t,
  appRegion,
}) => {
  const country = _get(data, 'address.country', '');
  const region = _get(data, 'address.region', '');
  const postalCode = _get(data, 'address.postalCode', '');
  const address = _get(data, 'address');

  return (
    <Row>
      <Column columns={{ tablet: 6 }}>
        <SummaryField
          content={data.firstName}
          titleKey={`${tKey}.first_name.label`}
          {...generateCyElemId(FIRST_NAME, window)}
        />
      </Column>
      <Column columns={{ tablet: 6 }}>
        <SummaryField
          content={data.lastName}
          titleKey={`${tKey}.last_name.label`}
          {...generateCyElemId(LAST_NAME, window)}
        />
      </Column>
      <Column columns={{ tablet: 6 }}>
        <SummaryField
          content={dateUtils.formatDate(merchantCountry, data.dateOfBirth)}
          titleKey={`${tKey}.dateOfBirth.label`}
          {...generateCyElemId(DATE_OF_BIRTH, window)}
        />
      </Column>
      {hasPersonalId && (
        <Column columns={{ tablet: 6 }}>
          <SummaryField
            titleKey={`${tKey}.personal_id.${
              personalIdUtils.getField(appRegion, merchantCountry, country).translationKey
            }.label_summary`}
            content={t(`${tKey}.personal_id.value`)}
            {...generateCyElemId(PERSONAL_ID, window)}
          />
        </Column>
      )}
      {hasAddressData(address) && (
        <Column columns={{ tablet: 6 }}>
          <SummaryField
            content={data.address.country}
            titleKey={`${tKey}.country.label`}
            {...generateCyElemId(ADDRESS_COUNTRY, window)}
          />
        </Column>
      )}
      {postalCode && (
        <Column columns={{ tablet: 6 }}>
          <SummaryField
            content={postalCode}
            titleKey={`${tKey}.postal_code.label`}
            {...generateCyElemId(POSTAL_CODE, window)}
          />
        </Column>
      )}
      {region && (
        <Column columns={{ tablet: 6 }}>
          <SummaryField
            content={
              country === 'US' ? t(`products-fe.options.us_states.${region}`, {}, region) : region
            }
            titleKey={`${tKey}.${country === 'US' ? 'state' : 'region'}.label`}
            {...generateCyElemId(REGION, window)}
          />
        </Column>
      )}
      {hasAddressData(address) && (
        <Column columns={{ tablet: 6 }}>
          <SummaryField
            content={data.address.city}
            titleKey={`${tKey}.city.label`}
            {...generateCyElemId(CITY, window)}
          />
        </Column>
      )}
      {hasAddressData(address) && (
        <Column columns={{ tablet: 6 }}>
          <SummaryField
            content={data.address.street}
            titleKey={`${tKey}.address.label`}
            {...generateCyElemId(STREET, window)}
          />
        </Column>
      )}
    </Row>
  );
};

SummaryPerson.propTypes = {
  appRegion: PropTypes.oneOf(Object.values(REGIONS)).isRequired,
  merchantCountry: PropTypes.string.isRequired,
  stakeholder: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default connect(state => ({
  t: translator(state),
  merchantCountry: getMerchantCountryKey(state),
  appRegion: merchantRegion(state),
}))(SummaryPerson);
